/**
 * Per-route SEO: keeps <title>, <meta name="description"> and <meta name="robots">
 * in sync with the current page. Single source of truth - no react-helmet
 * dependency. Also keeps the matching Open Graph + Twitter Card tags aligned
 * so social previews don't fall back to the global index.html copy.
 *
 * Usage:
 *   useSeo({
 *     title: "Pricing - Business Name Protection by NBR Group",
 *     description: "Simple, affordable protection for your UK business name. £15/mo or £155/yr - same protection, your choice.",
 *   });
 *
 *   // Transactional pages we don't want Google indexing:
 *   useSeo({ title: "Your details - Checkout", noindex: true });
 *
 * Notes:
 *   - The brand suffix " - Business Name Protection by NBR Group" is appended
 *     automatically unless `title` already ends with the brand block. Pass
 *     `appendBrand: false` if a page wants a fully bespoke title.
 *   - Set `canonical: "https://protectmybusinessname.co.uk/..."` to set the
 *     <link rel="canonical">. Defaults to the current window.location.href
 *     minus query / hash, which is correct for most pages.
 */
import { useEffect } from "react";

const BRAND_SUFFIX = "Business Name Protection by NBR Group";
const DEFAULT_DESCRIPTION =
  "Business Name Protection by NBR Group - the UK's only dedicated business name protection service. Check, protect and secure your business name before problems arise.";

function upsertMeta(attr, name, content) {
  if (!content) return;
  const sel = `meta[${attr}="${name}"]`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href) {
  if (!href) return;
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

export function useSeo({
  title,
  description,
  canonical,
  noindex = false,
  appendBrand = true,
} = {}) {
  useEffect(() => {
    const finalDescription = description || DEFAULT_DESCRIPTION;
    let finalTitle = title || BRAND_SUFFIX;
    // Only append the brand suffix if the title doesn't already mention the
    // product or company name - avoids the "… - Business Name Protection -
    // Business Name Protection by NBR Group" double-up on titles that already
    // carry the brand inline (e.g. "Pricing - Business Name Protection from £15/mo").
    const alreadyBranded = /business name protection|nbr group/i.test(finalTitle);
    if (appendBrand && title && !alreadyBranded) {
      finalTitle = `${title} - ${BRAND_SUFFIX}`;
    }
    document.title = finalTitle;
    upsertMeta("name", "description", finalDescription);
    upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");
    // Social previews - match the on-page SEO so shared links look right
    upsertMeta("property", "og:title", finalTitle);
    upsertMeta("property", "og:description", finalDescription);
    upsertMeta("property", "og:type", "website");
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", finalTitle);
    upsertMeta("name", "twitter:description", finalDescription);
    // Canonical - default to the current URL without query/hash
    let canonicalHref = canonical;
    if (!canonicalHref && typeof window !== "undefined") {
      canonicalHref = `${window.location.origin}${window.location.pathname}`;
    }
    upsertCanonical(canonicalHref);
    upsertMeta("property", "og:url", canonicalHref);
  }, [title, description, canonical, noindex, appendBrand]);
}
